/**
 * Field Grouping Utility
 *
 * Provides functions to categorize device fields into logical groups
 * based on field naming conventions.
 */

import { HardwareFieldDefinition } from '../services/typesService';
import { DeviceDetail } from '../services/deviceService';

/**
 * Represents a categorized field with display metadata
 */
export interface CategorizedField {
  name: string;           // Original field name (e.g., "mqtt_host")
  displayName: string;    // Display name (e.g., "Host")
  value: any;
  type: 'text' | 'number' | 'password' | 'boolean' | 'array' | 'json';
  editable: boolean;
}

/**
 * Represents a group of categorized fields
 */
export interface FieldGroupData {
  code: string;
  name: string;
  displayOrder: number;
  fields: CategorizedField[];
  isSpecial?: boolean;  // For special groups like 'additional'
}

/**
 * Configuration for field categorization
 */
interface FieldConfig {
  group: string;
  displayName: string;
  type: CategorizedField['type'];
  editable: boolean;
}

/**
 * Field configuration mapping
 * Defines which group each field belongs to and its display properties
 */
const FIELD_CONFIG: Record<string, FieldConfig> = {
  // MQTT fields
  mqtt_host: { group: 'mqtt', displayName: 'Host', type: 'text', editable: true },
  mqtt_port: { group: 'mqtt', displayName: 'Port', type: 'number', editable: true },
  mqtt_user: { group: 'mqtt', displayName: 'User', type: 'text', editable: true },
  mqtt_pass: { group: 'mqtt', displayName: 'Password', type: 'password', editable: true },
  mqtt_voice_dsts: { group: 'mqtt', displayName: 'Voice Destinations', type: 'array', editable: false },

  // Syslog fields
  syslog_host: { group: 'syslog', displayName: 'Host', type: 'text', editable: true },
  syslog_port: { group: 'syslog', displayName: 'Port', type: 'number', editable: true },
  syslog_facility: { group: 'syslog', displayName: 'Facility', type: 'text', editable: true },

  // Provisioning fields
  provisioning: { group: 'provisioning', displayName: 'URL', type: 'text', editable: true },
  keepalive_timer: { group: 'provisioning', displayName: 'Keepalive Timer', type: 'number', editable: true },

  // General fields
  password: { group: 'general', displayName: 'Device Password', type: 'password', editable: true },
  // conf47Type ("voice / local_alarm / dtmf_server") is intentionally NOT
  // surfaced as a user-facing field. The column + wire payload are kept
  // for retro-compatibility with field-deployed firmware; the value is
  // server-defaulted to "voice" on POST. Hardware-specific configuration
  // should be modeled via HardwareFieldDefinition rows instead.
  needsRefresh: { group: 'general', displayName: 'Needs Refresh', type: 'boolean', editable: true },

  // Special fields
  jsonAdditionalConfig: { group: 'additional', displayName: 'Additional Fields', type: 'json', editable: true },
};

/**
 * Default group display order
 */
const DEFAULT_GROUP_ORDER: Record<string, number> = {
  mqtt: 10,
  syslog: 20,
  provisioning: 30,
  general: 40,
  additional: 100,
};

/**
 * Default group names (fallback if API doesn't provide them)
 */
const DEFAULT_GROUP_NAMES: Record<string, string> = {
  mqtt: 'MQTT Configuration',
  syslog: 'Syslog Configuration',
  provisioning: 'Provisioning',
  general: 'General',
  additional: 'Additional Fields',
  network: 'Network',
  security: 'Security',
};

/**
 * Auto-detect group from field name based on prefix
 */
export function getFieldGroup(fieldName: string): string {
  // Check if field has explicit configuration
  if (FIELD_CONFIG[fieldName]) {
    return FIELD_CONFIG[fieldName].group;
  }

  // Auto-detect based on prefix
  if (fieldName.startsWith('mqtt_')) return 'mqtt';
  if (fieldName.startsWith('syslog_')) return 'syslog';
  if (fieldName.startsWith('prov_') || fieldName === 'provisioning') return 'provisioning';
  if (fieldName.startsWith('net_') || fieldName.startsWith('network_')) return 'network';
  if (fieldName.startsWith('sec_') || fieldName.startsWith('security_')) return 'security';

  return 'general';
}

/**
 * Extract display name from field name
 * mqtt_host → Host
 * syslog_facility → Facility
 * keepalive_timer → Keepalive Timer
 */
export function getDisplayName(fieldName: string): string {
  // Check if field has explicit configuration
  if (FIELD_CONFIG[fieldName]) {
    return FIELD_CONFIG[fieldName].displayName;
  }

  // Auto-generate display name
  const parts = fieldName.split('_');

  // Remove group prefix for known groups
  const prefixes = ['mqtt', 'syslog', 'prov', 'net', 'sec'];
  if (prefixes.includes(parts[0].toLowerCase())) {
    return parts.slice(1)
      .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(' ');
  }

  // Default: capitalize each word
  return parts
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get field type based on field name and value
 */
export function getFieldType(fieldName: string, value: any): CategorizedField['type'] {
  if (FIELD_CONFIG[fieldName]) {
    return FIELD_CONFIG[fieldName].type;
  }

  // Auto-detect from name
  if (fieldName.includes('pass') || fieldName.includes('secret')) return 'password';
  if (fieldName.includes('port')) return 'number';

  // Auto-detect from value
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object' && value !== null) return 'json';

  return 'text';
}

/**
 * Check if field is editable
 */
export function isFieldEditable(fieldName: string): boolean {
  if (FIELD_CONFIG[fieldName]) {
    return FIELD_CONFIG[fieldName].editable;
  }
  return true; // Default to editable for custom fields
}

/**
 * Categorize device fields using the hardware-type SCHEMA as the source
 * of truth instead of the top-level DeviceDetail columns. Only fields
 * that are actually defined on the hardware type's field_definitions
 * are shown — everything else (vestigial raw columns like syslog_* for
 * a hardware type that doesn't declare them) is excluded.
 *
 * Values are read from `device.effective_config` where available so the
 * Settings tab reflects the assembled result of the backend config
 * assembly pipeline. Top-level device columns (like conf47Type,
 * needsRefresh) are still used as fallback for cases where a schema
 * field matches a device column but has no effective_config entry.
 *
 * Groups come from the schema's `grouping_name` / `grouping_code`, with
 * a fallback to the generic grouping detection for fields missing
 * grouping metadata. Fields are sorted by `display_order` within each
 * group (not alphabetically) so schema authors control the ordering.
 */
export function categorizeDeviceFieldsFromSchema(
  device: DeviceDetail | null,
  schema: HardwareFieldDefinition[],
): FieldGroupData[] {
  if (!device || schema.length === 0) return [];

  const effective = (device.effective_config || {}) as Record<string, unknown>;
  // Deep-cast the device so we can read ad-hoc top-level keys as fallback
  // without TypeScript fighting us.
  const deviceAsAny = device as unknown as Record<string, unknown>;

  // Map schema value_type to the narrower CategorizedField type union.
  const mapType = (vt: string): CategorizedField['type'] => {
    switch (vt) {
      case 'number':
      case 'port':
        return 'number';
      case 'password':
        return 'password';
      case 'boolean':
        return 'boolean';
      case 'json':
        return 'json';
      default:
        // string | url | hostname | email | ip_address | dropdown | select
        return 'text';
    }
  };

  // Build groups keyed by grouping_code (falling back to grouping_name
  // slug, then to 'general' when neither is set on the field def).
  const groupMap = new Map<string, FieldGroupData>();

  for (const def of schema) {
    const code =
      def.grouping_code ||
      (def.grouping_name ? def.grouping_name.toLowerCase().replace(/\s+/g, '_') : 'general');
    const name = def.grouping_name || DEFAULT_GROUP_NAMES[code] || 'General';

    if (!groupMap.has(code)) {
      groupMap.set(code, {
        code,
        name,
        displayOrder: DEFAULT_GROUP_ORDER[code] ?? 50,
        fields: [],
      });
    }

    // Value resolution: effective_config first, then the raw device
    // column (for fields like conf47Type / needsRefresh that aren't in
    // the assembly pipeline), then undefined.
    let value: unknown = effective[def.field_name];
    if (value === undefined) {
      value = deviceAsAny[def.field_name];
    }

    groupMap.get(code)!.fields.push({
      name: def.field_name,
      displayName: def.display_name,
      value,
      type: mapType(def.value_type),
      editable: true,
    });
  }

  // Preserve the schema's own display_order within each group by
  // sorting AFTER insertion (input is already sorted by the backend,
  // but the spread above doesn't guarantee per-group order).
  groupMap.forEach((group) => {
    group.fields.sort((a, b) => {
      const aDef = schema.find((s) => s.field_name === a.name);
      const bDef = schema.find((s) => s.field_name === b.name);
      return (aDef?.display_order ?? 0) - (bDef?.display_order ?? 0);
    });
  });

  return Array.from(groupMap.values())
    .filter((g) => g.fields.length > 0)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Get editable fields from categorized groups
 * Returns a flat object suitable for form state
 */
export function getEditableFields(groups: FieldGroupData[]): Record<string, any> {
  const result: Record<string, any> = {};

  groups.forEach(group => {
    if (group.isSpecial) return; // Skip special groups like 'additional'

    group.fields.forEach(field => {
      if (field.editable) {
        result[field.name] = field.value;
      }
    });
  });

  return result;
}

/**
 * Build override object from changed fields
 */
export function buildOverrides(
  original: Record<string, any>,
  updated: Record<string, any>
): Record<string, any> {
  const overrides: Record<string, any> = {};

  Object.entries(updated).forEach(([key, value]) => {
    if (original[key] !== value) {
      overrides[key] = value;
    }
  });

  return overrides;
}
